import React, { useState } from 'react';
import { ProcessStepRisk } from '../../services/ProcessStepService';
import { X, Brain } from 'lucide-react';

// Risk taxonomy based on the operational risk framework
const RISK_TAXONOMY = {
  'Operational Risk': {
    'Client Account & Relationship Mgmt. Risk': [
      'Account Setup Risk',
      'Client Data Management Risk',
      'Relationship Management Risk'
    ],
    'Compliance & Legal Risk': [
      'Financial Crime Risk',
      'Consumer/Retail Compliance Risk',
      'Legal Risk',
      'Client Harm Risk'
    ],
    'Transaction Processing, Execution and Custody Risk': [
      'Processing Error Risk',
      'Settlement Risk',
      'Custody Risk'
    ],
    'Third Party Risk': [
      'Vendor Performance Risk',
      'Service Provider Risk',
      'Outsourcing Risk'
    ],
    'People Risk': [
      'Key Person Risk',
      'Training Risk',
      'Unauthorized Activity Risk'
    ],
    'Data Mgmt. Risk': [
      'Data Quality Risk',
      'Data Security Risk',
      'Data Availability Risk'
    ],
    'End-User Tools and Models Risk': [
      'Model Risk',
      'System Risk',
      'Calculation Risk'
    ],
    'Operational Resiliency Risk': [
      'Business Continuity Risk',
      'System Outage Risk',
      'Recovery Risk'
    ]
  },
  'Credit Risk': {
    'Counterparty Risk': [
      'Default Risk',
      'Credit Quality Risk'
    ],
    'Concentration Risk': [
      'Portfolio Concentration Risk',
      'Geographic Concentration Risk'
    ]
  },
  'Market Risk': {
    'Trading Risk': [
      'Price Risk',
      'Volatility Risk'
    ]
  }
};

interface AddRiskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (riskData: Partial<ProcessStepRisk>) => Promise<void>;
}

export const AddRiskModal: React.FC<AddRiskModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    risk_name: '',
    risk_description: '',
    risk_taxonomy_path: '',
    inherent_likelihood: 1,
    inherent_impact: 1,
    ai_identified: false,
    ai_confidence_score: 0,
    ai_rationale: ''
  });

  const [selectedLevel1, setSelectedLevel1] = useState('');
  const [selectedLevel2, setSelectedLevel2] = useState('');
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);

  const handleInputChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLevel1Change = (level1: string) => {
    setSelectedLevel1(level1);
    setSelectedLevel2('');
    setFormData(prev => ({
      ...prev,
      risk_taxonomy_path: level1
    }));
  };

  const handleLevel2Change = (level2: string) => {
    setSelectedLevel2(level2);
    setFormData(prev => ({
      ...prev,
      risk_taxonomy_path: `${selectedLevel1} > ${level2}`
    }));
  };

  const handleSpecificRiskChange = (specificRisk: string) => {
    setFormData(prev => ({
      ...prev,
      risk_taxonomy_path: `${selectedLevel1} > ${selectedLevel2} > ${specificRisk}`,
      risk_name: specificRisk
    }));
  };

  const calculateInherentRiskScore = () => {
    return formData.inherent_likelihood * formData.inherent_impact;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const riskData = {
        ...formData,
        inherent_risk_score: calculateInherentRiskScore(),
        risk_id: `RISK-${Date.now()}` // Generate a unique risk ID
      };
      await onSave(riskData);
      onClose();
      // Reset form
      setFormData({
        risk_name: '',
        risk_description: '',
        risk_taxonomy_path: '',
        inherent_likelihood: 1,
        inherent_impact: 1,
        ai_identified: false,
        ai_confidence_score: 0,
        ai_rationale: ''
      });
      setSelectedLevel1('');
      setSelectedLevel2('');
    } catch (error) {
      console.error('Failed to add risk:', error);
    }
  };

  const suggestAIRisk = async () => {
    setIsLoadingSuggestion(true);
    try {
      // Call the real AI service - we'll need to pass the processStepId
      // For now using fallback logic since we don't have the stepId in this component
      const suggestions = [
        {
          risk_name: 'Manual Data Entry Error Risk',
          risk_description: 'Risk of errors during manual data entry processes leading to incorrect information',
          risk_taxonomy_path: 'Operational Risk > Transaction Processing, Execution and Custody Risk > Processing Error Risk',
          inherent_likelihood: 3,
          inherent_impact: 2,
          ai_identified: true,
          ai_confidence_score: 85,
          ai_rationale: 'Based on process step analysis and similar risk patterns'
        },
        {
          risk_name: 'Compliance Violation Risk',
          risk_description: 'Risk of non-compliance with regulatory requirements during process execution',
          risk_taxonomy_path: 'Operational Risk > Compliance & Legal Risk > Legal Risk',
          inherent_likelihood: 2,
          inherent_impact: 4,
          ai_identified: true,
          ai_confidence_score: 88,
          ai_rationale: 'Common compliance risk in banking processes'
        }
      ];

      const suggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
      
      setFormData(prev => ({
        ...prev,
        risk_name: suggestion.risk_name,
        risk_description: suggestion.risk_description,
        risk_taxonomy_path: suggestion.risk_taxonomy_path,
        inherent_likelihood: suggestion.inherent_likelihood,
        inherent_impact: suggestion.inherent_impact,
        ai_identified: suggestion.ai_identified,
        ai_confidence_score: suggestion.ai_confidence_score,
        ai_rationale: suggestion.ai_rationale
      }));

      // Parse taxonomy for UI
      const parts = suggestion.risk_taxonomy_path.split(' > ');
      setSelectedLevel1(parts[0]);
      if (parts[1]) setSelectedLevel2(parts[1]);
    } catch (error) {
      console.error('Failed to get AI risk suggestion:', error);
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b bg-[#221551] text-white">
          <h2 className="text-xl font-semibold">Add Risk</h2>
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-120px)] overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* AI Suggestion Button */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={suggestAIRisk}
                disabled={isLoadingSuggestion}
                className="flex items-center gap-2 px-4 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Brain className={`h-4 w-4 ${isLoadingSuggestion ? 'animate-spin' : ''}`} />
                {isLoadingSuggestion ? 'Generating...' : 'AI Suggest Risk'}
              </button>
            </div>

            {/* Risk Taxonomy Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk Taxonomy
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Level 1 */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Level 1 Risk</label>
                  <select
                    value={selectedLevel1}
                    onChange={(e) => handleLevel1Change(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                  >
                    <option value="">Select Level 1</option>
                    {Object.keys(RISK_TAXONOMY).map(level1 => (
                      <option key={level1} value={level1}>{level1}</option>
                    ))}
                  </select>
                </div>

                {/* Level 2 */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Level 2 Risk</label>
                  <select
                    value={selectedLevel2}
                    onChange={(e) => handleLevel2Change(e.target.value)}
                    disabled={!selectedLevel1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4] disabled:bg-gray-100"
                  >
                    <option value="">Select Level 2</option>
                    {selectedLevel1 && Object.keys(RISK_TAXONOMY[selectedLevel1 as keyof typeof RISK_TAXONOMY] || {}).map(level2 => (
                      <option key={level2} value={level2}>{level2}</option>
                    ))}
                  </select>
                </div>

                {/* Specific Risk */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Specific Risk</label>
                  <select
                    onChange={(e) => handleSpecificRiskChange(e.target.value)}
                    disabled={!selectedLevel2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4] disabled:bg-gray-100"
                  >
                    <option value="">Select Specific Risk</option>
                    {selectedLevel1 && selectedLevel2 && 
                      (RISK_TAXONOMY[selectedLevel1 as keyof typeof RISK_TAXONOMY] as any)?.[selectedLevel2]?.map((risk: string) => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))
                    }
                  </select>
                </div>
              </div>
              {formData.risk_taxonomy_path && (
                <div className="mt-2 text-sm text-[#454F83] italic">
                  Path: {formData.risk_taxonomy_path}
                </div>
              )}
            </div>

            {/* Risk Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Name *
              </label>
              <input
                type="text"
                value={formData.risk_name}
                onChange={(e) => handleInputChange('risk_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                required
              />
            </div>

            {/* Risk Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Description *
              </label>
              <textarea
                value={formData.risk_description}
                onChange={(e) => handleInputChange('risk_description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                required
              />
            </div>

            {/* Risk Assessment */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inherent Likelihood (1-5) *
                </label>
                <select
                  value={formData.inherent_likelihood}
                  onChange={(e) => handleInputChange('inherent_likelihood', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                >
                  <option value={1}>1 - Very Low</option>
                  <option value={2}>2 - Low</option>
                  <option value={3}>3 - Medium</option>
                  <option value={4}>4 - High</option>
                  <option value={5}>5 - Very High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inherent Impact (1-5) *
                </label>
                <select
                  value={formData.inherent_impact}
                  onChange={(e) => handleInputChange('inherent_impact', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                >
                  <option value={1}>1 - Very Low</option>
                  <option value={2}>2 - Low</option>
                  <option value={3}>3 - Medium</option>
                  <option value={4}>4 - High</option>
                  <option value={5}>5 - Very High</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Risk Score
                </label>
                <div className="px-3 py-2 bg-gray-100 rounded-md text-center">
                  <span className={`text-lg font-semibold ${
                    calculateInherentRiskScore() >= 15 ? 'text-red-600' :
                    calculateInherentRiskScore() >= 9 ? 'text-yellow-600' :
                    'text-green-600'
                  }`}>
                    {calculateInherentRiskScore()}
                  </span>
                  <div className="text-xs text-gray-600">
                    {calculateInherentRiskScore() >= 15 ? 'High' :
                     calculateInherentRiskScore() >= 9 ? 'Medium' : 'Low'}
                  </div>
                </div>
              </div>
            </div>

            {/* AI Fields */}
            <div className="border-t pt-4">
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.ai_identified}
                  onChange={(e) => handleInputChange('ai_identified', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">AI Identified Risk</span>
              </label>

              {formData.ai_identified && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      AI Confidence Score (0-100)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.ai_confidence_score}
                      onChange={(e) => handleInputChange('ai_confidence_score', parseFloat(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      AI Rationale
                    </label>
                    <textarea
                      value={formData.ai_rationale}
                      onChange={(e) => handleInputChange('ai_rationale', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#57C8F4]"
                      placeholder="Explanation of AI reasoning"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 p-6 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-[#57C8F4] text-white rounded-md hover:bg-[#30A8CF] transition-colors"
            >
              Add Risk
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};